<script setup lang="ts">
  import { Icon, Radio, Typo, Tag, Picture } from '@travelata-libs/ui-kit';
  import { formatMoney, useViewport } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { CAPACITY_ICONS, FOR_FREE_TEXT, OPTION_TITLES } from '../lib';
  import { Capacity, type DirectionOption, type Option } from '../Transfer.types';

  interface DirectionOptionItemProps {
    modelValue: `${DirectionOption}`;
    option: Option;
  }

  interface DirectionOptionItemEmits {
    (e: 'update:modelValue', value: DirectionOption): void;
  }

  const props = defineProps<DirectionOptionItemProps>();

  const emit = defineEmits<DirectionOptionItemEmits>();

  const innerValue = computed({
    get() {
      return props.modelValue as DirectionOption;
    },
    set(value: DirectionOption) {
      emit('update:modelValue', value);
    },
  });

  const capacityItems = computed(() => {
    return [
      {
        icon: CAPACITY_ICONS[Capacity.Adult],
        count: props.option.adultCount,
      },
      {
        icon: CAPACITY_ICONS[Capacity.Luggage],
        count: props.option.luggageCount,
      },
    ];
  });

  const label = computed(() => OPTION_TITLES[props.option.value]);
  const priceInfo = computed(() => (props.option.includedInPrice ? FOR_FREE_TEXT : formatMoney(props.option.price)));
  const imagePath = computed(() => `transfer/${props.option.value}.png`);

  const onCardClick = () => {
    innerValue.value = props.option.value;
  };

  const { isMobile } = useViewport();
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div class="tl-transfer-option">
    <!-- @todo: Заменить на компонент Card, когда он будет готов -->
    <template v-if="isMobile">
      <div
        :class="[
          'tl-transfer-option__card',
          {
            'tl-transfer-option__card_selected': option.value === innerValue,
          },
        ]"
        @click="onCardClick"
      >
        <Picture :path="imagePath" :high-resolution="false" class="tl-transfer-option__img" />
        <Typo variant="caption-s-400" class="tl-transfer-option__label">{{ label }}</Typo>
        <Typo variant="body-l-500" class="tl-transfer-option__price">{{ priceInfo }}</Typo>

        <div class="tl-transfer-option__info">
          <div v-for="(capacity, index) in capacityItems" :key="index" class="tl-transfer-option__capacity">
            <Icon class="tl-transfer-option__capacity-icon" :name="capacity.icon" size="xs" />
            <Typo variant="caption-s-400">{{ ` × ${capacity.count}` }}</Typo>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <Radio v-model="innerValue" :value="option.value" name="transfer-option-group" size="l">
        <div class="tl-transfer-option__radio">
          <Picture :path="imagePath" :high-resolution="false" class="tl-transfer-option__img" />
          <Typo variant="body-l-500" class="tl-transfer-option__label">{{ label }}</Typo>
        </div>
      </Radio>

      <div class="tl-transfer-option__info">
        <div v-for="(capacity, index) in capacityItems" :key="index" class="tl-transfer-option__capacity">
          <Icon class="tl-transfer-option__capacity-icon" :name="capacity.icon" size="s" />
          <Typo variant="body-m-500">{{ ` × ${capacity.count}` }}</Typo>
        </div>

        <Tag size="s" appearance="tertiary" color="black" font-weight="medium">
          {{ priceInfo }}
        </Tag>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .tl-transfer-option {
    border-bottom: 1px dashed var(--color-text-tertiary);
    display: flex;
    justify-content: space-between;
    padding-bottom: var(--spacing-m);

    @media (max-width: $breakpoint-mobile-max) {
      border-bottom: none;
      justify-content: flex-start;
      padding-bottom: 0;
    }

    &__radio {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__card {
      border: 1px solid transparent;
      border-radius: var(--rounding-m);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      padding: var(--spacing-xxs);
      transition: var(--transition-default);

      &_selected {
        border-color: var(--color-brand-primary);
      }
    }

    &__img {
      height: 27px;
      width: 80px;

      @media (max-width: $breakpoint-mobile-max) {
        height: 25px;
        width: 75px;
      }
    }

    &__price,
    &__label {
      color: var(--color-text-primary);
    }

    &__info {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__capacity {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
    }

    &__capacity-icon {
      fill: var(--color-text-secondary);
      flex-shrink: 0;
    }
  }
</style>
